import { computed, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { Feature, GeocodeEarthParams } from '@/api/utils/geocodeearth'
import { getGeocodeEarthFeatures } from '@/api/utils/utils'
import { useGeolocationStore } from '@/store/geolocation/geolocationStore'
import { defineStore } from 'pinia'
import { useGoogleMapsStore } from '../google-maps/googleMapsStore'

const googleMapsStore = useGoogleMapsStore()

// any value in case of the use of the autocomplete of google places api
interface IAutocompleteLocation {
  label: string //  f.properties.label,
  value:
    | any
    | {
        label: string //  f.properties.label,
        country: string // f.properties.country,
        addressLine: string //  f.properties.name,
        postalCode: string // f.properties.postalcode,
        city: string // f.properties.locality,
        latitude: string // f.geometry?.coordinates[1]?.toString(),
        longitude: string //  f.geometry?.coordinates[0]?.toString(),
      }
}

export interface IAutocompleteLocationWithId {
  label: string //  f.properties.label,
  value: string // id
}

interface IAutocompleteOptionalParams {
  layers?: string
  latitude?: string
  longitude?: string
  country?: string
}

export const useAutocompleteLocationStore = defineStore(
  'autocomplete-location',
  () => {
    const autocompleteLocationList = ref<IAutocompleteLocation[] | []>([])
    const loadingLocation = ref(false)
    const selectedLocation = ref<IAutocompleteLocation>(null)
    const hasCityProp = computed(
      () =>
        Object.prototype.hasOwnProperty.call(selectedLocation.value, 'value') &&
        Object.prototype.hasOwnProperty.call(selectedLocation.value, 'label') &&
        Object.prototype.hasOwnProperty.call(
          selectedLocation.value.value,
          'city',
        ) &&
        selectedLocation.value.value.city,
    )

    /**
     * Fonction pour générer les paramètres d'autocompletion
     *
     *
     * @param {string} text : string,
     * @param {string} layers : address | country | null,
     * @param {string} latitude : ex: 48.033245,
     * @param {string} longitude : ex: 2.033245,
     * @param {string} country : string,
     * */
    const generateAutocompleteParams = (
      text: string,
      { layers, country }: IAutocompleteOptionalParams = {},
    ): GeocodeEarthParams => {
      const geolocationStore = useGeolocationStore()
      const params: GeocodeEarthParams = {
        text,
      }

      if (country) {
        params['boundary.country'] = country
      }

      if (layers) {
        params['layers'] = layers
      }

      if (!geolocationStore.storedLocationIsEmpty) {
        params['focus.point.lat'] = geolocationStore.getLat()
        params['focus.point.lon'] = geolocationStore.getLon()
      }

      return params
    }

    // Service d'autocomplétion d'adresse avec l'API GOOGLE MAPS
    const autocompleteRequest = useDebounceFn(async text => {
      if (!text) {
        loadingLocation.value = false
        return
      }
      try {
        const suggestionsPlaces =
          await googleMapsStore.getAutocompleteSuggestionsPlaces(text, [])
        autocompleteLocationList.value = suggestionsPlaces
      } catch (error) {
        throw new Error(`Error fetching data: ${error}`)
      } finally {
        loadingLocation.value = false
      }
    }, 200)

    // Service d'autocomplétion d'adresse avec Geocode earth
    const autocompleteLocationRequest = useDebounceFn(
      async (
        text: string,
        { layers, country }: IAutocompleteOptionalParams = {},
      ) => {
        if (!text) return []

        loadingLocation.value = true
        try {
          // get list of close cities / addresses / others
          const res = await getGeocodeEarthFeatures(
            generateAutocompleteParams(text, {
              layers,
              country,
            }),
          )
          const { data } = res
          const extractedValues = data.features.map(
            (f: Feature): IAutocompleteLocation => {
              return {
                label: f.properties.label,
                value: {
                  label: f.properties.label,
                  country: f.properties.country,
                  addressLine: f.properties.name,
                  postalCode: f.properties.postalcode,
                  city: f.properties.locality,
                  latitude: f.geometry?.coordinates[1]?.toString(),
                  longitude: f.geometry?.coordinates[0]?.toString(),
                },
              }
            },
          )
          autocompleteLocationList.value = extractedValues
        } catch (error) {
          console.error(`Error fetching data: ${error}`)
        } finally {
          loadingLocation.value = false
        }
      },
      100,
    )

    return {
      autocompleteLocationList,
      loadingLocation,
      selectedLocation,
      hasCityProp,
      autocompleteLocationRequest,
      autocompleteRequest,
    }
  },
)
